import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';

import type { AssociationSettingsHandle } from './association-settings-handle';

import { useAccountingPermissions } from '@/hooks/use-accounting';
import { useUpdateAssociation } from '@/hooks/use-data';
import { usePristinePayload } from '@/hooks/use-pristine-payload';
import type { Association } from '@/types/models';
import { Alert } from '@/utils/alert';

interface Props {
	/** Edit mode: the existing association whose copy is being edited. Omit for
	 *  the create (draft) flow — the section starts blank and `save(id)` is
	 *  called by the parent after the association is created. */
	association?: Association;
	/** When true, the parent owns saving via the imperative handle and this
	 *  section's own "Save" button is not rendered. */
	hideSaveButton?: boolean;
}

/** A cleared field is sent as `null` — `undefined` is dropped from the JSON body and
 *  the API would leave the old copy in place. */
const orNull = (s: string): string | null => s.trim() || null;

/**
 * Per-association overrides for the printed copy (rendered by `utils/document-copy.ts`):
 *  - `statementLegalText` — the bottom "memo" paragraph. The literal token
 *    `$###` is substituted with the active late-fee amount at print time.
 *    The hard-coded FDCPA notice is appended automatically and is NOT editable.
 *  - `statementCalloutText` — the yellow callout box body (online-payment plug).
 *  - `invoiceLegalText` / `invoiceCalloutText` — the same two boxes on an invoice
 *    (#678); blank falls back to the statement copy.
 */
export const AssociationStatementSettings = forwardRef<AssociationSettingsHandle, Props>(({ association, hideSaveButton }, ref) => {
	const { canModify } = useAccountingPermissions();
	const update = useUpdateAssociation();
	const [legal, setLegal] = useState(association?.statementLegalText ?? '');
	const [callout, setCallout] = useState(association?.statementCalloutText ?? '');
	const [invoiceLegal, setInvoiceLegal] = useState(association?.invoiceLegalText ?? '');
	const [invoiceCallout, setInvoiceCallout] = useState(association?.invoiceCalloutText ?? '');

	useEffect(() => {
		setLegal(association?.statementLegalText ?? '');
		setCallout(association?.statementCalloutText ?? '');
		setInvoiceLegal(association?.invoiceLegalText ?? '');
		setInvoiceCallout(association?.invoiceCalloutText ?? '');
	}, [
		association?.id, association?.statementLegalText, association?.statementCalloutText,
		association?.invoiceLegalText, association?.invoiceCalloutText,
	]);

	const body = useCallback(() => ({
		statementLegalText: orNull(legal),
		statementCalloutText: orNull(callout),
		invoiceLegalText: orNull(invoiceLegal),
		invoiceCalloutText: orNull(invoiceCallout),
	}), [legal, callout, invoiceLegal, invoiceCallout]);
	const { isDirty, markSaved } = usePristinePayload(body);

	const persist = useCallback(async (associationId: string) => {
		await update.mutateAsync({ id: associationId, data: body() });
		markSaved();
	}, [body, update, markSaved]);

	// This section had NO skip condition at all, so every association save rewrote the
	// statement copy whether or not anyone touched it.
	useImperativeHandle(ref, () => ({
		save: async (associationId: string) => {
			if (!isDirty) return;
			await persist(associationId);
		},
	}), [isDirty, persist]);

	const save = async () => {
		if (!association) return;
		try {
			await persist(association.id);
		} catch (e) {
			Alert.alert('Save failed', e instanceof Error ? e.message : 'Unknown error');
		}
	};

	return (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Statement &amp; invoice copy</Text>
			<Text style={styles.subTitle}>Statement copy</Text>
			<Text style={styles.help}>
				Bottom memo paragraph. Tokens pulled from the active late-fee rule:
				{' '}<Text style={styles.kbd}>$###</Text> (fee amount) and
				{' '}<Text style={styles.kbd}>{'{{grace}}'}</Text> (grace period as
				&quot;fifteen (15)&quot;). The Federal Debt Collections Act notice is appended
				automatically and cannot be removed.
			</Text>
			<TextInput
				style={[styles.input, styles.multiline]}
				value={legal}
				onChangeText={setLegal}
				editable={canModify}
				multiline
				numberOfLines={6}
				placeholder="All amount(s) due shall be payable in full within {{grace}} days of the first of each month. Thereafter, a late fee of $### …"
				placeholderTextColor="#bdbdbd"
			/>

			<Text style={[styles.label, { marginTop: 14 }]}>Yellow callout box</Text>
			<Text style={styles.help}>
				Optional online-payment plug shown on the right side of the statement. Printed exactly as typed —
				tokens like <Text style={styles.kbd}>$###</Text> are NOT filled in here.
			</Text>
			<TextInput
				style={[styles.input, styles.multiline]}
				value={callout}
				onChangeText={setCallout}
				editable={canModify}
				multiline
				numberOfLines={4}
				placeholder="Leave blank for the default."
				placeholderTextColor="#bdbdbd"
			/>

			<Text style={[styles.subTitle, { marginTop: 18 }]}>Invoice copy</Text>
			<Text style={styles.help}>
				Leave blank to use the statement copy. Both invoice boxes fill in these tokens:
				{' '}<Text style={styles.kbd}>$###</Text>, <Text style={styles.kbd}>{'{{grace}}'}</Text>, and — on
				invoices only — <Text style={styles.kbd}>{'{{dueDate}}'}</Text> (the invoice&apos;s due date, e.g.
				&quot;November 15, 2026&quot;; the invoice date plus the grace period if it has none),
				{' '}<Text style={styles.kbd}>{'{{invoiceDate}}'}</Text> and
				{' '}<Text style={styles.kbd}>{'{{invoiceNumber}}'}</Text>. If a box is blank and the invoice
				uses the statement copy instead, that copy prints just as it does on the statement.
			</Text>
			<Text style={styles.label}>Invoice legal text</Text>
			<TextInput
				style={[styles.input, styles.multiline]}
				value={invoiceLegal}
				onChangeText={setInvoiceLegal}
				editable={canModify}
				multiline
				numberOfLines={6}
				placeholder="Invoice legal text — leave blank to use the statement copy."
				placeholderTextColor="#bdbdbd"
			/>
			<Text style={[styles.label, { marginTop: 14 }]}>Invoice yellow callout box</Text>
			<TextInput
				style={[styles.input, styles.multiline]}
				value={invoiceCallout}
				onChangeText={setInvoiceCallout}
				editable={canModify}
				multiline
				numberOfLines={4}
				placeholder="Invoice callout — leave blank to use the statement copy."
				placeholderTextColor="#bdbdbd"
			/>

			{canModify && !hideSaveButton && (
				<Pressable
					onPress={save}
					style={[styles.primary, update.isPending && { opacity: 0.5 }]}
					disabled={update.isPending}
				>
					<Text style={styles.primaryLabel}>{update.isPending ? 'Saving…' : 'Save statement & invoice copy'}</Text>
				</Pressable>
			)}
		</View>
	);
});

AssociationStatementSettings.displayName = 'AssociationStatementSettings';

const styles = StyleSheet.create({
	section: { marginTop: 12, padding: 14, backgroundColor: '#f5f7fa', borderRadius: 8 },
	sectionTitle: { fontSize: 14, fontWeight: '700', color: '#212121', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 },
	subTitle: { fontSize: 13, fontWeight: '700', color: '#212121', marginBottom: 6, lineHeight: 18 },
	help: { fontSize: 12, color: '#546e7a', marginBottom: 8, lineHeight: 17 },
	label: { marginTop: 8, marginBottom: 4, fontSize: 11, color: '#546e7a', fontWeight: '600', textTransform: 'uppercase' },
	input: { borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6, padding: 10, fontSize: 13, backgroundColor: '#fff' },
	multiline: { minHeight: 110, textAlignVertical: 'top' },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center', marginTop: 12 },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	kbd: { fontFamily: 'monospace', backgroundColor: '#eceff1', paddingHorizontal: 4, borderRadius: 3 },
});
